import React, { useState, useEffect } from 'react';
import EcomBuddhaLogoIcon from '../../assets/icons/ecombuddha.png';
import { useNavigate, useLocation } from 'react-router-dom';
import BookDemoModal from './BookDemoModal';

const Navbar = ({ hideSignUp = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [showMobilePopup, setShowMobilePopup] = useState(false);
    const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);

    const isActive = (path) => {
        return location.pathname === path;
    };

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            setScrolled(scrollTop > 50);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Check if on auth page and mobile, show popup
    useEffect(() => {
        if (location.pathname === '/auth' && window.innerWidth < 768) {
            setShowMobilePopup(true);
        }
    }, [location.pathname]);

    const handleLoginClick = () => {
        navigate('/auth?mode=login');
        // Check if mobile after navigation
        setTimeout(() => {
            if (window.innerWidth < 768) {
                setShowMobilePopup(true);
            }
        }, 100);
    };

    // Landing site lives on a separate domain
    const LANDING_HOST = process.env.REACT_APP_LANDING_URL || '';
    const goExternal = (path) => {
        if (!LANDING_HOST) return;
        window.location.href = `${LANDING_HOST}${path}`;
    };

    const handleAboutClick = () => goExternal('/about');
    const handleContactClick = () => goExternal('/contact');
    const handlePricingClick = () => goExternal('/pricing');
    const handleLogoClick = () => goExternal('/home');
    const handleBlogsClick = () => goExternal('/blogs');
    const handleSolutionsClick = () => goExternal('/solutions');
    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 px-6 pt-1 pb-1 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : ''}`}>
            <div
                className="max-w-7xl mx-auto flex items-center justify-between"
                style={{ transform: 'scale(0.9)', transformOrigin: 'top center' }}
            >
                {/* Logo Section */}
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleLogoClick}
                        className="bg-transparent border-none cursor-pointer p-0"
                    >
                        <img
                            src={EcomBuddhaLogoIcon}
                            className="w-25 h-14 md:w-[150px] md:h-[80px]"
                            alt="EcomBuddha Logo"
                        />
                    </button>
                </div>

                {/* Navigation Links */}
                <div className="hidden md:flex items-center space-x-10 text-[20px]">
                    <button onClick={handleSolutionsClick} className={`transition-colors bg-transparent border-none cursor-pointer font-medium ${isActive('/solutions') ? 'text-[#0957B9]' : 'text-gray-700 hover:text-gray-900'}`}>
                        Solutions
                    </button>
                    <button onClick={handleAboutClick} className={`transition-colors bg-transparent border-none cursor-pointer font-medium ${isActive('/about') ? 'text-[#0957B9]' : 'text-gray-700 hover:text-gray-900'}`}>
                        About Us
                    </button>
                    <button onClick={handleBlogsClick} className={`transition-colors bg-transparent border-none cursor-pointer font-medium ${isActive('/blogs') ? 'text-[#0957B9]' : 'text-gray-700 hover:text-gray-900'}`}>
                        Blogs
                    </button>
                    <button onClick={handlePricingClick} className={`transition-colors bg-transparent border-none cursor-pointer font-medium ${isActive('/pricing') ? 'text-[#0957B9]' : 'text-gray-700 hover:text-gray-900'}`}>
                        Pricing
                    </button>
                    <button onClick={handleContactClick} className={`transition-colors bg-transparent border-none cursor-pointer font-medium ${isActive('/contact') ? 'text-[#0957B9]' : 'text-gray-700 hover:text-gray-900'}`}>
                        Contact Us
                    </button>
                </div>

                {/* Action Buttons - Desktop */}
                <div className="hidden md:flex items-center space-x-4">
                    {!hideSignUp && (
                        <button className="btn secondary-btn !text-base !py-2.5 !px-4" onClick={handleLoginClick}>
                            Login
                        </button>
                    )}
                    <button
                        className="btn primary-btn !text-base !py-2.5 !px-4"
                        onClick={() => setIsCallbackModalOpen(true)}
                    >
                        Book a Demo
                    </button>
                </div>

                {/* Mobile - Book a Demo and Hamburger */}
                <div className="flex md:hidden items-center space-x-2">
                    <button
                        className="btn primary-btn !text-sm !px-3"
                        style={{ paddingTop: '0.25rem', paddingBottom: '0.25rem', height: '34px', minHeight: '32px' }}
                        onClick={() => setIsCallbackModalOpen(true)}
                    >
                        Book a Demo
                    </button>
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="bg-transparent border-none cursor-pointer p-2"
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? (
                            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
                    <div className="px-6 py-4 space-y-4">
                        <button
                            onClick={() => {
                                handleSolutionsClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left py-2 text-lg font-medium transition-colors ${isActive('/solutions') ? 'text-[#0957B9]' : 'text-gray-700'}`}
                        >
                            Solutions
                        </button>
                        <button
                            onClick={() => {
                                handleAboutClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left py-2 text-lg font-medium transition-colors ${isActive('/about') ? 'text-[#0957B9]' : 'text-gray-700'}`}
                        >
                            About Us
                        </button>
                        <button
                            onClick={() => {
                                handlePricingClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left py-2 text-lg font-medium transition-colors ${isActive('/pricing') ? 'text-[#0957B9]' : 'text-gray-700'}`}
                        >
                            Pricing
                        </button>
                        <button
                            onClick={() => {
                                handleBlogsClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left py-2 text-lg font-medium transition-colors ${isActive('/blogs') ? 'text-[#0957B9]' : 'text-gray-700'}`}
                        >
                            Blogs
                        </button>
                        <button
                            onClick={() => {
                                handleContactClick();
                                setMobileMenuOpen(false);
                            }}
                            className={`block w-full text-left py-2 text-lg font-medium transition-colors ${isActive('/contact') ? 'text-[#0957B9]' : 'text-gray-700'}`}
                        >
                            Contact Us
                        </button>
                        {!hideSignUp && (
                            <button
                                onClick={() => {
                                    handleLoginClick();
                                    setMobileMenuOpen(false);
                                }}
                                className="btn secondary-btn !text-base !py-2.5 !px-4 w-full mt-4"
                            >
                                Login
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Mobile Popup Modal */}
            {showMobilePopup && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-800">Note</h3>
                            <button
                                onClick={() => setShowMobilePopup(false)}
                                className="text-gray-500 hover:text-gray-700 transition-colors"
                                aria-label="Close"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="text-gray-600 mb-6">
                            Ecombuddha works best on desktop. Switch devices for the full experience!
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setShowMobilePopup(false);
                                    goExternal('/home');
                                }}
                                className="btn secondary-btn flex-1"
                            >
                                Okay
                            </button>
                            <button
                                onClick={() => {
                                    setShowMobilePopup(false);
                                    setIsCallbackModalOpen(true);
                                }}
                                className="btn primary-btn flex-1"
                            >
                                Book a Demo
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <BookDemoModal
                isOpen={isCallbackModalOpen}
                onClose={() => setIsCallbackModalOpen(false)}
            />
        </nav>
    );
};

export default Navbar;